import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, CreditCard, CheckCircle2, XCircle } from 'lucide-react';

// Admin tool: registers a domain for Apple Pay via Square's official
// POST /v2/apple-pay/domains API, bypassing the dashboard's unreliable
// verification crawler. Shows Square's raw response (or exact error) verbatim.
export default function AdminApplePay() {
  const [domain, setDomain] = useState('www.motionreality.co.uk');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const register = async () => {
    setError('');
    setResult(null);
    if (!domain.trim()) { setError('Enter the domain to register.'); return; }
    setLoading(true);
    try {
      const res = await base44.functions.invoke('squareRegisterApplePayDomain', {
        domain_name: domain.trim(),
      });
      setResult(res.data);
    } catch (err) {
      setError(err?.message || 'Could not reach the registration function. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const squareSuccess = result?.success === true;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center gap-2 mb-2">
        <CreditCard className="w-4 h-4 text-primary" />
        <p className="text-primary text-[10px] font-bold tracking-widest">PAYMENTS</p>
      </div>
      <h1 className="text-white font-black text-2xl uppercase mb-1">Apple Pay Domains</h1>
      <p className="text-white/40 text-sm mb-6 max-w-2xl">
        Registers your domain directly with Square's official Apple Pay API. This bypasses the Square dashboard's
        verification crawler (which returns 'partial response' errors) — Apple performs the domain check instead.
        The exact Square response is shown below, verbatim.
      </p>

      <div className="border border-white/10 bg-[#111115] p-6">
        <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">DOMAIN TO REGISTER</label>
        <input
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className="seo-input"
          placeholder="www.motionreality.co.uk"
        />
        <p className="text-white/30 text-[11px] mt-2">
          The domain-association file must be live at <code className="text-white/50">https://{'{domain}'}/.well-known/apple-developer-merchantid-domain-association</code> before registering.
        </p>

        {error && <div className="mt-4 px-4 py-3 bg-red-500/5 border border-red-500/30 text-red-300 text-xs">{error}</div>}

        <button
          onClick={register}
          disabled={loading}
          className="mt-5 w-full bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-3.5 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> REGISTERING…</> : <>REGISTER DOMAIN</>}
        </button>
      </div>

      {result && (
        <div className={`border p-6 mt-6 ${squareSuccess ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
          <div className="flex items-center gap-2 mb-3">
            {squareSuccess
              ? <CheckCircle2 className="w-5 h-5 text-green-400" />
              : <XCircle className="w-5 h-5 text-red-400" />}
            <p className={`font-bold text-sm ${squareSuccess ? 'text-green-300' : 'text-red-300'}`}>
              {squareSuccess ? 'Registration succeeded' : 'Square returned an error'}
            </p>
            {result.http_status && (
              <span className="text-white/30 text-xs font-mono ml-auto">HTTP {result.http_status}</span>
            )}
          </div>
          <p className="text-white/50 text-xs mb-3">
            {squareSuccess
              ? 'Square accepted the domain. Apple may take a few minutes to complete its check — after that, Apple Pay works on this domain.'
              : 'Copy the raw response below into a Square support ticket if needed.'}
          </p>
          <pre className="text-[11px] font-mono text-white/70 bg-black/40 border border-white/10 p-4 overflow-x-auto whitespace-pre-wrap break-all">
            {JSON.stringify(result.square_response, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}